import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../components/Custom3dotString";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  approveStockAdjustment,
  approveStockIssueRequest,
  getPendingStockReview,
} from "../../../redux/reducers/clinicSlice";
import {
  approveApprovalRequest,
  getApprovals,
  rejectApprovalRequest,
} from "../../../redux/reducers/globalSettingsSlice";
import { AppColors } from "../../../util/AppColors";
import { StockAdjustList } from "../../../util/arrayList";
import { plusBtn } from "../../../util/CommonIconStyle";
import { requestAndTransferRetriveEnum } from "../../../util/enum";
import { tblGrayBack } from "../../../util/object";
import { canWriteModule } from "../../../util/roleModuleAccess";
import StockAdjustment from "../../CommonScreens/Stock/StockAdjustment";

const ROLE_TO_USER_TYPES = {
  FRONT_DESK: ["ROLE_RECEPTION"],
  BILLING: ["ROLE_BILLING"],
  INVENTORY_MANAGER: ["ROLE_INVENTORY"],
  BRANCH_MANAGER: ["ROLE_CLINICADMIN"], //ROLE_CLINIC
  CLINIC_OWNER: ["ROLE_CLINIC"], //ROLE_ADMIN
  ROLE_CLINICSUPERADMIN: ["ROLE_CLINICSUPERADMIN"],
};

const TRANSFER_RULE_KEYS = {
  InternalTransfer: ["INTERNAL_TRANSFER", "INTERNAL_TRASFER"],
  "Internal Transfer": ["INTERNAL_TRANSFER", "INTERNAL_TRASFER"],
  ExternalTransfer: ["EXTERNAL_TRANSFER", "EXTERNAL_TRASFER"],
  "External Transfer": ["EXTERNAL_TRANSFER", "EXTERNAL_TRASFER"],
};

const getCurrentUserRoleNames = (profile) => {
  const names = new Set();
  const storedRole = localStorage.getItem("role");
  if (storedRole) names.add(storedRole);
  if (profile?.role) names.add(profile.role);
  if (profile?.userRole) names.add(profile.userRole);
  const types = profile?.userType || profile?.roles || profile?.roleList || [];
  if (Array.isArray(types)) {
    types.forEach((role) => {
      const name =
        typeof role === "string" ? role : role?.name || role?.authority;
      if (name) names.add(name);
    });
  }
  return [...names].filter(Boolean);
};

const userMatchesApprovalRule = (rule, profile) => {
  if (!rule) return false;
  const ruleRoles = Array.isArray(rule?.roles)
    ? rule.roles.filter(Boolean)
    : [];
  const ruleUserIds = (Array.isArray(rule?.userIds) ? rule.userIds : [])
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));

  const userRoleNames = getCurrentUserRoleNames(profile).map((name) =>
    String(name).trim(),
  );
  const roleMatched =
    ruleRoles.length === 0
      ? false
      : ruleRoles.some((code) => {
          const allowedTypes = (ROLE_TO_USER_TYPES[code] || [code]).map(
            (type) => String(type).trim(),
          );
          return allowedTypes.some(
            (type) =>
              userRoleNames.includes(type) ||
              userRoleNames.includes(String(code).trim()),
          );
        });

  const currentUserId = Number(
    profile?.id ?? profile?.doctorId ?? profile?.userId,
  );
  const userIdMatched =
    ruleUserIds.length > 0 &&
    !Number.isNaN(currentUserId) &&
    ruleUserIds.includes(currentUserId);

  // Roles only (no userIds) → role match is enough
  if (!ruleUserIds.length) return roleMatched;

  // Specific people selected under roles:
  // - matching role can still approve (e.g. CLINIC_OWNER → ROLE_CLINIC)
  // - OR current userId is in the selected userIds list
  return roleMatched || userIdMatched;
};

const getMatchedRuleForRequest = (approvalRules, ruleKeys = [], amount) => {
  if (!ruleKeys.length) return null;

  const rulesMap = approvalRules?.rules ?? approvalRules;
  if (!rulesMap || typeof rulesMap !== "object") return null;

  const config = ruleKeys.map((key) => rulesMap[key]).find(Boolean) ?? null;
  if (!config) return null;
  if (config?.autoApproval) return null;

  const rules = Array.isArray(config?.rules)
    ? config.rules
    : Array.isArray(config)
      ? config
      : [];
  if (!rules.length) return null;

  // Amount tiers only when both bounds are usable numbers.
  // Inventory rules often use minValue: 0, maxValue: null (open-ended).
  const hasClosedAmountTiers = rules.some(
    (rule) =>
      rule?.minValue != null &&
      rule?.maxValue != null &&
      !Number.isNaN(Number(rule.minValue)) &&
      !Number.isNaN(Number(rule.maxValue)),
  );

  if (!hasClosedAmountTiers) return rules[0] ?? null;

  const value = Math.abs(Number(amount ?? 0));
  return (
    rules.find((rule) => {
      const min = rule?.minValue == null ? 0 : Number(rule.minValue);
      const max =
        rule?.maxValue == null
          ? Number.POSITIVE_INFINITY
          : Number(rule.maxValue);
      return (
        !Number.isNaN(min) && !Number.isNaN(max) && value >= min && value <= max
      );
    }) ??
    rules[0] ??
    null
  );
};

const tblHdrs = [
  "recordId",
  "type",
  "remarks",
  "supplierCustomer",
  "date",
  "details",
  "actions",
];
const adjustViewTblHdrs = [
  "product",
  "adjustedQuantity",
  "uom",
  "batchNumber",
  "location",
];
const issueViewTblHdrs = [
  "product",
  "quantity",
  "fromLocation",
  "toLocation",
  "status",
];
const nameExpan = {
  recordId: "#",
  type: "Type",
  remarks: "Reference / Reason",
  supplierCustomer: "Supplier/Customer / Requested By",
  date: "Date",
  details: "Details",
  actions: "",
};
const initSearch = {
  startDate: null,
  endDate: null,
  customerName: "",
  supplierName: "",
  invoiceNumber: "",
};

const getApproveValue = (item) =>
  item?.approve ?? item?.apporve ?? item?.approved;

const isRejectedRecord = (item) => {
  const reason = String(item?.rejectReason ?? "").trim();
  if (!reason) return false;
  const approve = getApproveValue(item);
  return (
    approve === false ||
    approve === "N" ||
    String(approve).toLowerCase() === "false"
  );
};

const PendingReview = () => {
  const dispatch = useDispatch();
  const canManageInventory = canWriteModule("inventory");
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [viewModVsble, setViewModVsble] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [page, setPage] = useState(1);
  const [adjustModVsble, setAdjustModVsble] = useState(false);
  const [decisionModVsble, setDecisionModVsble] = useState(false);
  const [decisionType, setDecisionType] = useState(null);
  const [decisionRemarks, setDecisionRemarks] = useState("");
  const [decisionRemarksErr, setDecisionRemarksErr] = useState("");
  const itemSize = 10;
  const { pendingStockReview } = useSelector((state) => state?.clinic);
  const approvalRules = useSelector(
    (state) => state?.globalSettings?.approvalRules,
  );
  const approvals = useSelector((state) => state?.globalSettings?.approvals);
  const user = localStorage.getItem("user");
  const profile = user ? JSON.parse(user) : null;

  useEffect(() => {
    // dispatch(getApprovalRules({ silent: true }));
    dispatch(getApprovals({ silent: true }));
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.startDate,
    searchValues?.endDate,
    searchValues?.customerName,
    searchValues?.supplierName,
    searchValues?.invoiceNumber,
    selectedFilter,
  ]);

  const getResolvedDateRange = () => {
    const startRaw = searchValues?.startDate;
    const endRaw = searchValues?.endDate;
    if (!startRaw && !endRaw) return { startDate: null, endDate: null };

    const start = startRaw ? moment(new Date(startRaw)) : null;
    let end = endRaw ? moment(new Date(endRaw)) : null;

    if (start && !end) end = start.clone();
    if (!start && end) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      };
    }
    if (start && end && end.isBefore(start, "day")) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: start.format("YYYY-MM-DD"),
      };
    }

    return {
      startDate: start?.format("YYYY-MM-DD") ?? null,
      endDate: end?.format("YYYY-MM-DD") ?? null,
    };
  };

  const onApiCall = (pge = 1) => {
    setPage(pge);
    const { startDate, endDate } = getResolvedDateRange();
    let reqUrl = `?status=pending&approve=N&page=${pge}&itemSize=${itemSize}`;
    if (startDate) reqUrl += `&startDate=${startDate}`;
    if (endDate) reqUrl += `&endDate=${endDate}`;
    if (searchValues?.supplierName?.length > 0) {
      reqUrl += `&supplierName=${searchValues?.supplierName}`;
    }
    if (searchValues?.customerName?.length > 0) {
      reqUrl += `&customerName=${searchValues?.customerName}`;
    }
    if (searchValues?.invoiceNumber?.length > 0) {
      reqUrl += `&invoiceNumber=${searchValues?.invoiceNumber}`;
    }
    if (selectedFilter?.value) reqUrl += `&type=${selectedFilter?.value}`;
    dispatch(getPendingStockReview(reqUrl));
  };

  const tableList = () => {
    const adjustments = (pendingStockReview?.stockAdjustments ?? []).map(
      (li) => {
        const reqQty = (li?.stockAdjustDetails ?? []).reduce(
          (acc, obj) => acc + (Number(obj?.quantity) ?? 0),
          0,
        );
        return {
          ...li,
          recordKind: "adjustment",
          recordId: li?.adjustId,
          type: li?.type ?? "",
          date: li?.createdDate
            ? moment(new Date(li.createdDate)).format("DD MMM YYYY")
            : "",
          supplierCustomer: li?.supplierName ?? li?.customerName ?? "",
          details: `${Math.abs(reqQty)} items adjusted`,
          isRejected: isRejectedRecord(li),
        };
      },
    );

    const issues = (pendingStockReview?.stockIssues ?? []).map((li) => {
      const reqQty = (li?.stockIssueDetails ?? []).reduce(
        (acc, obj) =>
          acc + (Number(obj?.quantity ?? obj?.issueQuantity ?? 0) || 0),
        0,
      );
      const typeLabel =
        requestAndTransferRetriveEnum?.[li?.requestType] ??
        li?.requestType ??
        "";
      return {
        ...li,
        recordKind: "issue",
        recordId: li?.issueId,
        type: typeLabel,
        date: li?.requestedDate
          ? moment(new Date(li.requestedDate)).format("DD MMM YYYY")
          : li?.createdDate
            ? moment(new Date(li.createdDate)).format("DD MMM YYYY")
            : "",
        supplierCustomer: li?.requestedName ?? "",
        remarks: li?.rejectReason || li?.remarks || "",
        details: `${Math.abs(reqQty)} items`,
        isRejected: isRejectedRecord(li),
      };
    });

    return [...adjustments, ...issues].sort((a, b) => {
      const aTime = new Date(a?.createdDate ?? a?.requestedDate ?? 0).getTime();
      const bTime = new Date(b?.createdDate ?? b?.requestedDate ?? 0).getTime();
      return bTime - aTime;
    });
  };

  const onCloseMod = (typ) => {
    setAdjustModVsble(false);
    if (typ === "apiSuccess") onApiCall();
  };

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const handleCreate = () => {
    setAdjustModVsble(true);
  };

  const handleView = (itm) => {
    setSelectedItem(itm);
    setViewModVsble(true);
  };

  const onCloseViewMod = () => {
    setViewModVsble(false);
    setSelectedItem(null);
  };

  const canDecideItem = (item) => {
    if (!item || item.isRejected) return false;

    let ruleKeys = ["COUNT_CORRECTION"];
    if (item.recordKind === "issue") {
      ruleKeys =
        TRANSFER_RULE_KEYS[item?.requestType] ||
        TRANSFER_RULE_KEYS[item?.type] ||
        [];
    }

    // Types without approval-rule config stay actionable
    if (!ruleKeys?.length) return true;

    const amount =
      item?.totalAmt ??
      item?.total ??
      item?.amount ??
      (item?.stockAdjustDetails ?? item?.stockIssueDetails ?? []).reduce(
        (acc, obj) =>
          acc +
          (Number(obj?.quantity ?? obj?.issueQuantity ?? obj?.total ?? 0) || 0),
        0,
      );

    const matchedRule = getMatchedRuleForRequest(
      approvalRules,
      ruleKeys,
      amount,
    );
    if (!matchedRule) return false;
    return userMatchesApprovalRule(matchedRule, profile);
  };

  const handleApprove = (itm) => {
    setSelectedItem(itm);
    setDecisionType("approve");
    setDecisionRemarks("");
    setDecisionRemarksErr("");
    setDecisionModVsble(true);
  };

  const handleReject = (itm) => {
    setSelectedItem(itm);
    setDecisionType("reject");
    setDecisionRemarks("");
    setDecisionRemarksErr("");
    setDecisionModVsble(true);
  };

  const onCloseDecisionMod = () => {
    setDecisionModVsble(false);
    setDecisionType(null);
    setDecisionRemarks("");
    setDecisionRemarksErr("");
  };

  const onConfirmDecision = async () => {
    if (!selectedItem) return;

    const remarks = (decisionRemarks ?? "").trim();
    if (decisionType === "reject" && !remarks) {
      setDecisionRemarksErr("Reason is required");
      return;
    }

    if (selectedItem.recordKind === "adjustment") {
      const result = await dispatch(
        approveStockAdjustment({
          adjustId: selectedItem.adjustId,
          approve: decisionType === "approve" ? "Y" : "N",
          rejectReason: decisionType === "reject" ? remarks : "",
          skipSuccessToast: true,
        }),
      );
      if (!approveStockAdjustment.fulfilled.match(result)) return;
    } else {
      const result = await dispatch(
        approveStockIssueRequest({
          issueId: selectedItem.issueId,
          approve: decisionType === "approve",
          rejectReason: decisionType === "reject" ? remarks : "",
          skipSuccessToast: true,
        }),
      );
      if (!approveStockIssueRequest.fulfilled.match(result)) return;
    }

    const matchingApproval = (Array.isArray(approvals) ? approvals : []).find(
      (ap) => {
        const isPending = String(ap?.status || "").toLowerCase() === "pending";
        if (!isPending) return false;
        if (selectedItem.recordKind === "adjustment") {
          return (
            ap?.adjustId != null &&
            String(ap.adjustId) === String(selectedItem.adjustId)
          );
        }
        return (
          ap?.issueId != null &&
          String(ap.issueId) === String(selectedItem.issueId)
        );
      },
    );
    const approvalId = matchingApproval?.approvalId;
    const approvalRemarks =
      remarks || (decisionType === "approve" ? "Verified and approved" : "");

    if (approvalId) {
      if (decisionType === "approve") {
        const approvalRes = await dispatch(
          approveApprovalRequest({ approvalId, remarks: approvalRemarks }),
        );
        if (!approveApprovalRequest.fulfilled.match(approvalRes)) return;
      } else {
        const approvalRes = await dispatch(
          rejectApprovalRequest({ approvalId, remarks: approvalRemarks }),
        );
        if (!rejectApprovalRequest.fulfilled.match(approvalRes)) return;
      }
    }

    onCloseDecisionMod();
    onApiCall(page);
    dispatch(getApprovals({ silent: true }));
  };

  const getAdjustViewList = () => {
    if (!selectedItem || selectedItem.recordKind !== "adjustment") return [];
    return (selectedItem?.stockAdjustDetails ?? []).map((li) => ({
      product: li?.productName ?? "",
      adjustedQuantity: li?.quantity ?? "",
      uom: li?.uomName ?? "",
      batchNumber: li?.batchNumber ?? "",
      location: li?.locationName ?? "",
    }));
  };

  const getIssueViewList = () => {
    if (!selectedItem || selectedItem.recordKind !== "issue") return [];
    return (selectedItem?.stockIssueDetails ?? []).map((li) => ({
      product: li?.itemName ?? "",
      quantity: li?.quantity ?? li?.issueQuantity ?? "",
      fromLocation: li?.fromLocationName ?? "",
      toLocation: li?.toLocationName ?? "",
      status: li?.status ?? "",
    }));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value,
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
      setSelectedFilter(null);
    });
  };

  const rows = tableList();
  const totalRecords = pendingStockReview?.totalRecords ?? rows.length;

  return (
    <>
      <div className="com-mar back-white pv10">
        <div className="flex-row-ali-cen w100Per">
          <div className="flex-center w100Per">
            <div className="w13Per">
              <CustomTextField
                placeholder="Start Date"
                fullWidth
                name="startDate"
                handleChange={onSearchChange}
                value={searchValues?.startDate}
                backgroundColor={AppColors.lightBlue2}
                label="Start Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="End Date"
                fullWidth
                name="endDate"
                handleChange={onSearchChange}
                value={searchValues?.endDate}
                backgroundColor={AppColors.lightBlue2}
                label="End Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="Supplier"
                fullWidth
                name="supplierName"
                handleChange={onSearchChange}
                value={searchValues?.supplierName}
                backgroundColor={AppColors.lightBlue2}
                label="Supplier"
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="Customer"
                fullWidth
                name="customerName"
                handleChange={onSearchChange}
                value={searchValues?.customerName}
                backgroundColor={AppColors.lightBlue2}
                label="Customer"
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="Invoice Number"
                fullWidth
                name="invoiceNumber"
                handleChange={onSearchChange}
                value={searchValues?.invoiceNumber}
                backgroundColor={AppColors.lightBlue2}
                label="Invoice Number"
              />
            </div>
            <div className="w13Per ml10">
              <CustomSelect
                list={[
                  ...StockAdjustList,
                  { name: "Return on Sales", value: "sales" },
                  { name: "Internal Transfer", value: "InternalTransfer" },
                  { name: "External Transfer", value: "ExternalTransfer" },
                ]}
                fullWidth
                value={selectedFilter}
                handleChange={(e, value) => setSelectedFilter(value)}
                search
                label="Type"
                backgroundColor={AppColors.lightBlue2}
              />
            </div>
            <div className="ml10">
              <CustomButton text="Reset" smallBtn onClick={onReset} redBtn />
            </div>
            {canManageInventory ? (
              <div className="ml10">
                <CustomButton text="Create" smallBtn onClick={handleCreate} />
              </div>
            ) : null}
          </div>
        </div>

        <div className="ml20">
          <Typography className="font-medium fs12 gray28 mt5">
            Number of count: {totalRecords}
          </Typography>
        </div>
        <Box className="ph20">
          <TableContainer sx={{ maxHeight: 470 }} className="mb20 mt10">
            <Table
              sx={{ minWidth: 200 }}
              aria-labelledby="tableTitle"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  {tblHdrs?.map((hdr, i) => (
                    <TableCell
                      style={tblGrayBack}
                      className="txt-semi-bold fs12"
                      key={hdr + i}
                    >
                      <div>{nameExpan?.[hdr]}</div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="text-align-center h100">
                    <div className="no-rec">No records available</div>
                  </TableCell>
                </TableRow>
              )}
              <TableBody>
                {rows.map((li, ind) => (
                  <TableRow
                    key={`pending_${li.recordKind}_${li.recordId ?? ind}`}
                  >
                    {tblHdrs.map((hdr, i) => (
                      <TableCell key={`${ind}_${i}_${hdr}`} component="th">
                        <div>
                          {hdr === "remarks" ? (
                            <Custom3dotString
                              str={li?.[hdr]}
                              className="font-regular fs14"
                            />
                          ) : hdr === "actions" ? (
                            <div className="flex-row-ali-cen gap10">
                              {li.isRejected ? (
                                <span className="app-status-badge rejected fs12">
                                  Rejected
                                </span>
                              ) : canManageInventory && canDecideItem(li) ? (
                                <>
                                  <div>
                                    <CustomButton
                                      text="Approve"
                                      smallBtn
                                      onClick={(e) => {
                                        e?.stopPropagation?.();
                                        handleApprove(li);
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <CustomButton
                                      text="Reject"
                                      smallBtn
                                      redBtn
                                      onClick={(e) => {
                                        e?.stopPropagation?.();
                                        handleReject(li);
                                      }}
                                    />
                                  </div>
                                </>
                              ) : null}
                              <div
                                className="cursor"
                                onClick={() => handleView(li)}
                              >
                                <div className="flex-center">
                                  <VisibilityIcon sx={plusBtn} />
                                </div>
                                <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                                  View
                                </Typography>
                              </div>
                            </div>
                          ) : (
                            li?.[hdr]
                          )}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
              {Math.ceil(totalRecords / itemSize) > 1 ? (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={12}>
                      <div className="flex-end">
                        <Pagination
                          count={Math.ceil(totalRecords / itemSize)}
                          variant="outlined"
                          color="primary"
                          page={page}
                          onChange={handleChangePage}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </TableContainer>
        </Box>
      </div>

      {adjustModVsble ? (
        <StockAdjustment modVsble={adjustModVsble} onCloseMod={onCloseMod} />
      ) : null}

      <CustomModal
        open={viewModVsble}
        onClose={onCloseViewMod}
        header={
          selectedItem?.recordKind === "issue"
            ? "Stock Transfer"
            : "Stock Adjustment"
        }
        headerCenter
        modalWidth={90}
        modal
      >
        <div className="scroll-80vh">
          {selectedItem?.recordKind === "issue" ? (
            <>
              <Grid container spacing={2} className="mt5 ph20">
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <CustomTextField
                    label="Request Type"
                    disabled
                    fullWidth
                    value={selectedItem?.type ?? ""}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <CustomTextField
                    label="Requested Date"
                    disabled
                    fullWidth
                    value={selectedItem?.date ?? ""}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <CustomTextField
                    label="Requested By"
                    disabled
                    fullWidth
                    value={selectedItem?.requestedName ?? ""}
                  />
                </Grid>
                {selectedItem?.isRejected ? (
                  <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                    <CustomTextField
                      label="Reject Reason"
                      disabled
                      fullWidth
                      value={selectedItem?.rejectReason ?? ""}
                    />
                  </Grid>
                ) : null}
              </Grid>
              <CustomTable
                columns={issueViewTblHdrs}
                datas={getIssueViewList()}
                grey
              />
            </>
          ) : (
            <>
              <Grid container spacing={2} className="mt5 ph20">
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <CustomTextField
                    label="Type"
                    disabled
                    fullWidth
                    value={selectedItem?.type ?? ""}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <CustomTextField
                    label="Created Date"
                    disabled
                    fullWidth
                    mobileDate
                    value={selectedItem?.createdDate}
                  />
                </Grid>
                {["sales", "return on purchase"].includes(
                  selectedItem?.type?.toLowerCase?.() ?? "",
                ) ? (
                  <>
                    <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                      <CustomTextField
                        label="Invoice Number"
                        disabled
                        fullWidth
                        value={selectedItem?.invoiceNumber ?? ""}
                      />
                    </Grid>
                    {(
                      selectedItem?.type?.toLowerCase() === "sales"
                        ? selectedItem?.invoiceDate
                        : selectedItem?.billDate
                    ) ? (
                      <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                        <CustomTextField
                          label="Invoice Date"
                          disabled
                          fullWidth
                          mobileDate
                          value={
                            selectedItem?.type?.toLowerCase() === "sales"
                              ? selectedItem?.invoiceDate
                              : selectedItem?.billDate
                          }
                        />
                      </Grid>
                    ) : null}
                    <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                      <CustomTextField
                        label={`${
                          selectedItem?.type?.toLowerCase() === "sales"
                            ? "Customer"
                            : "Supplier"
                        } Name`}
                        disabled
                        fullWidth
                        value={
                          (selectedItem?.type?.toLowerCase() === "sales"
                            ? selectedItem?.customerName
                            : selectedItem?.supplierName) ?? ""
                        }
                      />
                    </Grid>
                  </>
                ) : null}
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                  <CustomTextField
                    label="Remarks"
                    disabled
                    fullWidth
                    value={selectedItem?.remarks}
                  />
                </Grid>
                {selectedItem?.isRejected ? (
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <CustomTextField
                      label="Reject Reason"
                      disabled
                      fullWidth
                      value={selectedItem?.rejectReason ?? ""}
                    />
                  </Grid>
                ) : null}
              </Grid>
              <CustomTable
                columns={adjustViewTblHdrs}
                datas={getAdjustViewList()}
                grey
              />
            </>
          )}
        </div>
      </CustomModal>

      <CustomModal
        open={decisionModVsble}
        onClose={onCloseDecisionMod}
        header={decisionType === "reject" ? "Reject" : "Approve"}
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10 mb10">
          {decisionType === "reject"
            ? "Are you sure you want to reject this request?"
            : "Are you sure you want to approve this request?"}
        </Typography>
        {decisionType === "reject" ? (
          <div className="ml10 mr10 mb10">
            <CustomTextField
              label="Reject Reason"
              fullWidth
              multiline
              rows={3}
              value={decisionRemarks}
              handleChange={(e) => {
                setDecisionRemarks(e?.target?.value ?? "");
                if (decisionRemarksErr) setDecisionRemarksErr("");
              }}
              error={!!decisionRemarksErr}
              helperText={decisionRemarksErr}
            />
          </div>
        ) : null}
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className="flex-center mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                smallBtn
                redBtn={decisionType === "reject"}
                onClick={onConfirmDecision}
              />
            </div>
            <div className="ml10">
              <CustomButton
                text="No"
                onClick={onCloseDecisionMod}
                smallBtn
                redBtn={decisionType !== "reject"}
              />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </>
  );
};

export default PendingReview;
